<template>
  <div class="toolbar-wrap" :class="isMobile?'mb-6':''">
    <h2 :class="isMobile?'pt-3 mx-2 toolbar-title':'mx-8'">{{ title.plural }}</h2>

    <v-row class="align-center mt-0 mx-6" :class="isMobile?'':'mx-3'">
      <!-- 👇 este es el bloque de botones: le agregamos clase -->
      <div class="d-flex align-center pb-0 toolbar-actions">
        <v-tooltip location="top">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="plain"
              class="square-btn"
              icon
              @click="openFilters"
              v-if="(currentView === 'table' || currentView === 'calendar' || currentView === 'kanban') && title.plural != 'Posters'"
            >
              <v-icon>mdi mdi-filter</v-icon>
              <v-badge
                v-if="activeFilterCount > 0"
                :content="activeFilterCount"
                color="primary"
                overlap
                style="position: absolute; top: 10px; right: 10px;"
              />
            </v-btn>
          </template>
          Filtros
        </v-tooltip>

        <v-tooltip location="top">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="plain"
              class="square-btn"
              icon
              @click="openPreferences"
            >
              <v-icon>mdi mdi-cog</v-icon>
            </v-btn>
          </template>
          Preferencias
        </v-tooltip>

        <v-tooltip location="top" v-if="title.plural == 'Ventas' || title.plural == 'Cotizaciones'|| title.plural == 'Clientes'|| title.plural == 'Prospectos'">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="plain"
              class="square-btn"
              icon
              @click="openExport"
            >
              <v-icon>mdi mdi-download</v-icon>
            </v-btn>
          </template>
          Exportar CSV
        </v-tooltip>

        <v-tooltip location="top" v-if="showTotalsToggle">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="plain"
              class="square-btn"
              icon
              :color="totalsVisible ? 'primary' : undefined"
              @click="$emit('toggle-totals')"
            >
              <v-icon>mdi-finance</v-icon>
            </v-btn>
          </template>
          Totales
        </v-tooltip>
      </div>
      <v-spacer/>

      <v-tabs
        v-if="showTabs && tabs?.length"
        v-model="selectedTab"
        class="mx-md-4 pt-7 pt-md-0"
        density="comfortable"
        color="primary"
        :stacked="isMobile"
      >
        <v-tab
          v-for="tab in tabs"
          :key="tab.value ?? 'all'"
          :value="tab.value"
          class="mr-8"
          style="height: 30px;"
        >
          {{ tab.label }}
        </v-tab>
      </v-tabs>

      <v-spacer/>

      <!-- el resto queda igual -->
      <div class="d-flex align-center pb-0 px-6" v-if="currentView == 'grid' || currentView == 'card'">
        Ordenar por:
        <v-autocomplete
          v-model="sortBy"
          :items="sortableHeaders"
          item-title="title"
          item-value="value"
          density="compact"
          style="max-width: 200px;"
          class="mx-4"
          hide-details
          variant="outlined"
        />
        <v-btn
          variant="plain"
          @click="direction === 'asc' ? (direction = 'desc') : (direction = 'asc')"
          density="compact"
          style="min-width: 0px;"
          class="px-0"
        >
          <v-icon v-if="direction === 'asc'">mdi-arrow-up</v-icon>
          <v-icon v-else>mdi-arrow-down</v-icon>
        </v-btn>
      </div>

      <div class="ml-auto d-flex justify-end pb-0">
        <slot name="before-create" />
        <v-btn
          color="primary"
          class="mr-6 mt-2 elevation-0"
          v-if="!isMobile && !!onCreate && $can(moduleName.toLowerCase()+'.create') && (title.plural != 'Ventas' || !IS_DOMINIOMEDIOS) && title.plural != 'Facturación' && title.plural != 'Continuidad' && title.plural != 'Cancelaciones'"
          @click="openCreateForm"
        >
          Crear {{title.singular}}
        </v-btn>
        <v-btn-toggle
          mandatory
          v-model="currentView"
          color="primary"
          variant="text"
          v-if="filteredViewModes.length>1"
        >
          <v-tooltip
            v-for="mode in filteredViewModes"
            :key="mode.value"
            location="top"
          >
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                class="square-btn ma-2"
                icon
                :value="mode.value"
              >
                <v-icon>{{ mode.icon }}</v-icon>
              </v-btn>
            </template>
            {{ mode.label }}
          </v-tooltip>
        </v-btn-toggle>
      </div>

      <v-btn v-if="isMobile && title.plural != 'Ventas' && title.plural != 'Facturación' && title.plural != 'Continuidad' && title.plural != 'Cancelaciones'" icon class="fab-btn" @click="openCreateForm">
        <v-icon :color="isDarkMode?'white':'black'">mdi-plus</v-icon>
      </v-btn>
    </v-row>
  </div>
</template>


<script lang="ts">
import { defineComponent, PropType, computed, ref, onMounted } from 'vue';
import { configRegistry } from '@/lib/configRegistry';
import { useRightDrawerStore } from '@/store/rightDrawer';
import { useFloatFormStore } from '@/store/floatForm';
import { ViewModes, ViewModeOptions } from '@/enums/viewModes';
import { useLayoutField } from '@/composables/useLayoutField';
import { useDisplay } from 'vuetify';
import { useExportDialogStore } from '@/store/exportDialog';

export default defineComponent({
  name: 'Toolbar',
  props: {
    moduleName: {
      type: String,
      required: true,
    },
    sortableHeaders: Array,
    config:String,
    onCreate: {
      type: Function as PropType<(data: Record<string, any>) => Promise<any>>,
      required: false,
    },
    exportCsv: {
      type: Function as PropType<(data: Record<string, any>) => Promise<any>>,
      required: false,
    },
    title: Object,
    availableViews: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => ['table', 'grid', 'card'], // fallback por si no se pasa
    },
    client: { type: Object, required: false },
    product_id: Number,
    medium: String,
    tabs: {
      type: Array as PropType<Array<{ label: string; value: any }>>,
      required: false,
      default: () => [],
    },
    tabFilterKey: {
      type: String,
      required: false,
      default: 'tab',
    },
    showTabs: {
      type: Boolean,
      default: false,
    },
    showTotalsToggle: {
      type: Boolean,
      default: false,
    },
    totalsVisible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggle-totals'],
  setup(props) {

    const { mobile } = useDisplay();
    const isMobile = computed(() => mobile.value);

    const isDarkMode = ref(window.matchMedia('(prefers-color-scheme: dark)').matches);
    window.matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', e => isDarkMode.value = e.matches);

    const moduleName = props.moduleName

    const currentView = useLayoutField(moduleName, 'currentView');
    const sortBy = useLayoutField(moduleName, 'sortBy');
    const direction = useLayoutField(moduleName, 'direction');

    const filters = useLayoutField(moduleName, 'filters');
    const defaultTabValue = computed(() => props.tabs?.[0]?.value ?? 'all');
    const selectedTab = computed({
      get: () => {
        const current = filters.value?.[props.tabFilterKey];
        const tabValues = (props.tabs || []).map((t) => t?.value);
        const currentExistsInTabs = tabValues.some((v) => v === current);

        // Si no hay filtro, usa el primer tab como seleccionado visualmente
        if (current === undefined || current === null) {
          return defaultTabValue.value;
        }

        // Si el filtro actual no existe en las tabs renderizadas (ej. cambio de modo),
        // hace fallback al default visual para evitar tab "sin selección".
        if (!currentExistsInTabs) {
          return defaultTabValue.value;
        }

        return current;
      },
      set: (val) => {
        const tabValues = (props.tabs || []).map((t) => t?.value);
        const valueExistsInCurrentTabs = tabValues.some((v) => v === val);
        // Evita escribir valores "fantasma" cuando cambian dinámicamente tabs/tabFilterKey
        // (ej. pasar de client_type_id a tag en modo fusión).
        if (!valueExistsInCurrentTabs) return;

        const next = { ...(filters.value || {}) };
        const clearTabSelected =
          val === null ||
          val === undefined ||
          val === 'all' ||
          (val === 0 && props.tabs?.some(t => t.value == null));

        if (clearTabSelected) {
          delete next[props.tabFilterKey];
        } else {
          next[props.tabFilterKey] = val;
        }
        filters.value = next;
      }
    });
    const filterConfig = ref<any[]>([]);
    onMounted(async () => {
      const loader = configRegistry[moduleName];
      if (!loader) return;
      try {
        const mod = await loader();
        filterConfig.value = mod.FILTERS || mod.filters || [];
      } catch (err) {
        console.error(`[Toolbar] Error cargando filtros: "${moduleName}"`, err);
      }
    });
    const activeFilterCount = computed(() => {
      return filterConfig.value.reduce((acc, filter) => {
        const val = filters.value?.[filter.value];
        if (val !== null && val !== undefined && val !== '') acc++;
        return acc;
      }, 0);
    });

    const { openDrawer } = useRightDrawerStore();

    const { openForm } = useFloatFormStore();

    const openFilters = () => {
      openDrawer({
        module: moduleName,
        config:props.config,
        component: 'Filters',
        drawerTitle: 'Filtros',
        drawerWidth: '400',
      });
    };

    const openPreferences = () => {
      openDrawer({
        module: moduleName,
        component: isMobile.value?'CardLayoutConfig':'Preferences',
        drawerTitle: 'Preferencias',
        drawerWidth: '400',
      });
    };

    const openExport = () => {
      const exportDialogStore = useExportDialogStore();
      exportDialogStore.openDialog({
        moduleName: moduleName,
        filters: filters.value,
        exportCsv: props.exportCsv,
        fileName: props.title?.plural
      });
    };

    const openCreateForm = () => {
      openForm({
        component: 'CreateForm',
        formTitle: 'Crear ' + props.title?.singular,
        formWidth: '1080',
        module: moduleName,
        props: {
          onCreate: props.onCreate,
          initialValues: {
            ...(props.client ? { client: props.client } : {}),
            ...(props.client ? { subject: props.client } : {}),
            ...(props.product_id ? { product_id: props.product_id, medium: props.medium } : {}),
          }
        }
      });
    }

    const filteredViewModes = computed(() =>
      ViewModeOptions.filter((mode) => props.availableViews.includes(mode.value))
    );

    const getHostnameSafe = (): string => {
      if (typeof window !== 'undefined' && window.location?.hostname) {
        return window.location.hostname;
      }
      return process.env.VITE_PUBLIC_HOST ?? process.env.SSR_HOST ?? '';
    };
    const IS_DOMINIOMEDIOS = getHostnameSafe() === 'crm.dominiomedios.com';

    return {
      currentView,
      sortBy,
      direction,
      openFilters,
      openPreferences,
      openCreateForm,
      ViewModes,
      filteredViewModes,
      moduleName,
      isMobile,
      activeFilterCount,
      openExport,
      isDarkMode,
      selectedTab,
      showTotalsToggle: props.showTotalsToggle,
      totalsVisible: props.totalsVisible,
      IS_DOMINIOMEDIOS
    };
  },
});
</script>

<style scoped>
/* contenedor relativo para poder posicionar acciones en mobile */
.toolbar-wrap {
  position: relative;
}

/* en mobile: título y acciones en la misma línea */
@media (max-width: 959.98px) {
  .toolbar-title {
    /* asegura que el título no choque con los botones */
    padding-right: 96px; /* espacio para 2–3 iconos */
    display: flex;
    align-items: center;
    min-height: 40px;
  }
  .toolbar-actions {
    position: absolute;
    top: 2px;            /* ajusta según tu h2 */
    right: 8px;
    display: flex !important;
    align-items: center;
    justify-content: flex-end;
    width: auto !important;
    max-width: none !important;
    padding: 0;          /* sin padding extra */
  }
}

/* desktop: todo como estaba (posición normal) */
@media (min-width: 960px) {
  .toolbar-actions {
    position: static;
  }
}

.fab-btn {
  position: fixed;
  bottom: 24px;
  right: 24px;
  border-radius: 50%;
  z-index: 10;
  width: 56px;
  height: 56px;
}
</style>
